import type { QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { parseLinkParams } from '@/features/clinic'
import { createRecaptcha, RecaptchaProvider, type RecaptchaFactory } from '@/features/bot'
import { createHostBridge, takeNonce, type HostBridge } from '@/features/frame-bridge'
import { DirectPage } from '@/pages/DirectPage'
import { EmbedNotFoundPage, EmbedPage } from '@/pages/EmbedPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import type { PatientEnv } from './env'
import { directLinkFor, resolveMode } from './mode'
import { Providers } from './providers'

export interface AppLocation {
  pathname: string
  search: string
}

/**
 * The patient root (Frontend §4.1): the path picks the mode: the direct link `/c/{slug}`, the embed iframe
 * `/c/{slug}/embed` (task 08), or the not-found page. Read once: a refresh starts over (UserFlows §2.4).
 *
 * The embed path opened top-level (`window.parent === window`) redirects to the direct link. Its host bridge is made
 * at boot, before the first render, so embed.js's `init` (posted on the iframe's `load`) is never missed.
 */
export function App({
  queryClient,
  env,
  location = window.location,
  bridge,
  frameParent,
  navigate = (url) => {
    window.location.replace(url)
  },
  recaptcha,
}: {
  queryClient: QueryClient
  env: PatientEnv
  /** Storybook passes the URL to render. */
  location?: AppLocation
  /** The embed's host bridge (boot makes it early; stories pass a stand-in). */
  bridge?: HostBridge
  /** The embed's parent window (default `window.parent`; FR tests pass a stand-in). */
  frameParent?: Window
  /** The top-level redirect of an embed path (FR tests pass a spy). */
  navigate?: (url: string) => void
  /** reCAPTCHA (task 16): Google's `enterprise.js` (or `VITE_RECAPTCHA_SCRIPT_URL`); FR tests and stories a fake. */
  recaptcha?: RecaptchaFactory
}) {
  const [recaptchaFactory] = useState<RecaptchaFactory>(
    () => recaptcha ?? ((keys) => createRecaptcha(keys, { scriptUrl: env.VITE_RECAPTCHA_SCRIPT_URL })),
  )
  const [mode] = useState(() => resolveMode(location.pathname, env.VITE_DEFAULT_CLINIC_SLUG))
  const [link] = useState(() => parseLinkParams(location.search))
  const [parent] = useState(() => frameParent ?? window.parent)
  const topLevel = mode.kind === 'embed' && bridge === undefined && parent === window
  const [frame] = useState<HostBridge | null>(() => {
    if (mode.kind !== 'embed' || topLevel) return null
    return bridge ?? createHostBridge({ win: window, parent, nonce: takeNonce(window) })
  })
  const redirectTo = topLevel ? (mode.slug !== null ? directLinkFor(mode.slug, location.search) : '/') : null
  useEffect(() => {
    if (redirectTo !== null) navigate(redirectTo)
  }, [redirectTo, navigate])

  let page = <NotFoundPage />
  if (mode.kind === 'direct') page = <DirectPage slug={mode.slug} link={link} />
  else if (mode.kind === 'embed' && frame) {
    page =
      mode.slug !== null ? (
        <EmbedPage slug={mode.slug} link={link} bridge={frame} />
      ) : (
        <EmbedNotFoundPage bridge={frame} />
      )
  } else if (topLevel) page = <></>
  return (
    <Providers queryClient={queryClient}>
      <RecaptchaProvider factory={recaptchaFactory}>{page}</RecaptchaProvider>
    </Providers>
  )
}
